import { ALL_RELATIONSHIP_DEFINITIONS } from './definitions';
import type { RelationshipDefinition, RelationshipLabel } from './types';

/**
 * Get all allowed relationship labels between two entity types
 *
 * This is the main function EdgeLabel will call to populate the dropdown.
 * Returns only relationships that are valid for the given source/target pair.
 *
 * @param sourceLabel - Source node's firstLabel (e.g., "Asset", "ADObject", "Port")
 * @param targetLabel - Target node's firstLabel (e.g., "Port", "ADObject", "Risk")
 * @returns Array of valid RelationshipLabel objects for the dropdown
 *
 * @example
 * getAllowedRelationships("Asset", "Port")
 * // Returns: [{ actual: "HAS_PORT", display: "Has Port" }]
 *
 * @example
 * getAllowedRelationships("Asset", "Asset")
 * // Returns: [{ actual: "DISCOVERED", display: "Discovered" }]
 *
 * @example
 * getAllowedRelationships("ADObject", "ADObject")
 * // Returns: [
 * //   { actual: "MemberOf", display: "Member Of" },
 * //   { actual: "AdminTo", display: "Admin To" },
 * //   ... (all 85 AD relationships)
 * // ]
 */
export function getAllowedRelationships(
  sourceLabel: string,
  targetLabel: string
): RelationshipLabel[] {
  const allowed: RelationshipLabel[] = [];

  for (const definition of ALL_RELATIONSHIP_DEFINITIONS) {
    if (isAllowedBetween(definition, sourceLabel, targetLabel)) {
      // Add ALL labels from this definition (aliasing support)
      allowed.push(...definition.labels);
    }
  }

  // Sort alphabetically by display name
  return allowed.sort((a, b) => a.display.localeCompare(b.display));
}

/**
 * Check if a relationship definition allows a specific source/target pair
 * Respects bidirectional flag for reverse lookups
 *
 * @param definition - Relationship definition to check
 * @param sourceLabel - Source entity label
 * @param targetLabel - Target entity label
 * @returns true if the source/target pair is allowed by this definition
 */
function isAllowedBetween(
  definition: RelationshipDefinition,
  sourceLabel: string,
  targetLabel: string
): boolean {
  for (const rule of definition.allowedBetween) {
    // Check forward direction (source → target)
    if (rule.source === sourceLabel && rule.target === targetLabel) {
      return true;
    }

    // Check reverse direction if bidirectional (target → source)
    if (
      definition.bidirectional &&
      rule.source === targetLabel &&
      rule.target === sourceLabel
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a specific relationship label is valid between two entity types
 * Useful for validation when a user selects a relationship
 *
 * @param sourceLabel - Source entity label
 * @param targetLabel - Target entity label
 * @param relationshipActual - The actual relationship type to validate
 * @returns true if the relationship is valid for this source/target pair
 *
 * @example
 * isValidRelationship("Asset", "Port", "HAS_PORT") // true
 * isValidRelationship("Port", "Asset", "HAS_PORT") // false (not bidirectional)
 * isValidRelationship("ADObject", "ADObject", "MemberOf") // true
 */
export function isValidRelationship(
  sourceLabel: string,
  targetLabel: string,
  relationshipActual: string
): boolean {
  const allowed = getAllowedRelationships(sourceLabel, targetLabel);
  return allowed.some(label => label.actual === relationshipActual);
}
